import RuleRow from 'owa-rules-option-common/lib/components/RuleRow';
import editInboxRule from '../actions/editInboxRule';
import enableDisableInboxRule from '../actions/enableDisableInboxRule';
import removeInboxRule from '../actions/removeInboxRule';
import { InboxRuleRowDragData } from '../store/schema/InboxRuleRowDragData';
import InboxRuleRowType from '../store/schema/InboxRuleRowType';
import store from '../store/store';
import { observer } from 'mobx-react';
import DragAndDroppable from 'owa-dnd/lib/components/DragAndDroppable';
import { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { lazyMoveInboxRule } from 'owa-inbox-rules-option';
import RuleDescription from 'owa-service/lib/contract/RuleDescription';
import strings from 'owa-strings/owa-options-strings';
import * as React from 'react';
import { isFeatureEnabled } from 'owa-feature-flags';
import canRunRule from '../utils/canRunRule';
import runRuleNow from '../actions/actionCreators/runRuleNow';
import { isBulkActionRunning, isBulkActionUninitialized } from 'owa-bulk-action-store';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import isRuleRunning from '../selectors/isRuleRunning';

const styles = require('./InboxRuleRow.scss');
let classNames = require('classnames/bind').bind(styles);

export interface InboxRuleRowProps {
    inboxRuleId: string;
}
@observer
export default class InboxRuleRow extends React.Component<InboxRuleRowProps, {}> {
    private shouldEnableEdit(inboxRuleRow: InboxRuleRowType): boolean {
        // Do not enable editing if the rule is not supported by OWA OR
        // rule has a clause type we don't support just yet
        // VSTS: 23316 and 23317 Add remaining conditions, actions, and exceptions
        // Check that the rule user wants to edit does not have any clauses that have
        // not been implemented yet.
        return (
            inboxRuleRow.SupportedByTask &&
            !inboxRuleRow.ApplySystemCategory &&
            !inboxRuleRow.HasClassification &&
            !inboxRuleRow.ExceptIfHasClassification &&
            !inboxRuleRow.FromSubscription &&
            !inboxRuleRow.ExceptIfFromSubscription &&
            !inboxRuleRow.SendTextMessageNotificationTo
        );
    }

    render() {
        const ruleId = this.props.inboxRuleId;
        const ruleRowIds = store.inboxRulesListState.inboxRuleIds;
        const inboxRuleRow = store.inboxRulesListState.inboxRules.get(ruleId);
        const descriptionText = this.getDescriptionText(inboxRuleRow.Description);
        const isRuleSupported = inboxRuleRow.SupportedByTask;
        const warningMessages = inboxRuleRow.WarningMessages;
        const isToggleDisabled = !!warningMessages;
        const isToggleChecked = !isToggleDisabled && inboxRuleRow.Enabled;
        const alertString = this.getAlertString(isRuleSupported, warningMessages);
        const inboxRuleRowClassName = classNames(
            inboxRuleRow.drop.isDragOver && styles.showBorderOnDroppedOver
        );
        const ruleIndex = ruleRowIds.indexOf(ruleId);
        const canRunRuleNow =
            canRunRule(inboxRuleRow) &&
            inboxRuleRow.SupportedByTask &&
            isFeatureEnabled('tri-bulkActionNotification') &&
            isFeatureEnabled('tri-runRulesNow');
        const shouldDisableRunRuleNow =
            isBulkActionRunning(folderNameToId('inbox')) ||
            isBulkActionUninitialized(folderNameToId('inbox'));

        return (
            <DragAndDroppable
                getDragData={this.getDragData}
                getDragPreview={this.getDragPreview}
                dropViewState={inboxRuleRow.drop}
                onDrop={this.onDrop}
                canDrop={this.canDrop}>
                <RuleRow
                    key={ruleId}
                    ruleName={inboxRuleRow.Name}
                    ruleDescription={isRuleSupported ? descriptionText : null}
                    isChecked={isToggleChecked}
                    isDisabled={isToggleDisabled}
                    enableDisableRule={this.onEnableDisableInboxRule}
                    removeRule={this.onRemoveInboxRule}
                    editRule={this.shouldEnableEdit(inboxRuleRow) ? this.onEditInboxRule : null}
                    moveRule={this.onMoveRule}
                    disableDownArrow={ruleIndex == ruleRowIds.length - 1}
                    disableUpArrow={ruleIndex == 0}
                    alertString={alertString}
                    customClassNames={inboxRuleRowClassName}
                    runRuleNow={canRunRuleNow ? this.runRuleNow : null}
                    disableRunRuleNow={shouldDisableRunRuleNow}
                    isRuleRunning={isRuleRunning(ruleId, folderNameToId('inbox'))}
                    onDoubleClick={
                        this.shouldEnableEdit(inboxRuleRow) ? this.onEditInboxRule : null
                    }
                />
            </DragAndDroppable>
        );
    }

    /**
     * Gets the description text for the given inbox rule
     * @param inboxRuleDescriptions the inbox rule clause descriptions
     * @return inbox rule description
     */
    private getDescriptionText(inboxRuleDescriptions: RuleDescription): string {
        let andDelimeter = ' ' + strings.options_inboxRules_and + ' ';
        let orDelimeter = ' ' + strings.options_inboxRules_or + ' ';
        let commaDelimeter = ', ';
        let period = '.';

        // Create the description string from all the clauses
        // For example: If <conditional clause 1>, <conditional clause 2> and <conditional clause 3>,
        // <action clause 1>, <action clause 2> and <action clause 3>,
        // except when <exception clause 1>, <exception clause 2> or <exception clause 3>
        let descriptionText = strings.options_inboxRules_if + ' ';

        // If there are no conditions, that means it is the 'any' clause so append the 'any' clause description
        if (inboxRuleDescriptions.ConditionDescriptions.length === 0) {
            descriptionText += strings.options_inboxRules_anyClauseDescription;
        } else {
            descriptionText += this.getDescriptionTextHelper(
                inboxRuleDescriptions.ConditionDescriptions,
                commaDelimeter,
                andDelimeter
            );
        }
        descriptionText += commaDelimeter;
        descriptionText += this.getDescriptionTextHelper(
            inboxRuleDescriptions.ActionDescriptions,
            commaDelimeter,
            andDelimeter
        );

        let exceptionDescriptions = inboxRuleDescriptions.ExceptionDescriptions;

        if (exceptionDescriptions.length > 0) {
            descriptionText += commaDelimeter + strings.options_inboxRules_exceptWhen + ' ';
            descriptionText += this.getDescriptionTextHelper(
                inboxRuleDescriptions.ExceptionDescriptions,
                commaDelimeter,
                orDelimeter
            );
        }

        descriptionText += period;

        return descriptionText;
    }

    /**
     * Gets the description text for a clause for the given inbox rule (conditional, action, exception)
     * @param descriptions the descriptions for an inbox rule's clause
     * @param midDelimeter the delimeter for between clauses except between the second to last and last clauses
     * @param lastDelimeter the delimeter for between the second to last and last clauses
     * @return the concatenated, user-ready description for an inbox rule's clause
     */
    private getDescriptionTextHelper(
        descriptions: string[],
        midDelimeter: string,
        lastDelimeter: string
    ): string {
        let descriptionText = '';

        for (let i = 0; i < descriptions.length; i++) {
            descriptionText += descriptions[i];

            // Clauses should be separated by ',' except for between the second to last and last clauses,
            // which should be separated by 'and'
            if (i < descriptions.length - 2) {
                descriptionText += midDelimeter;
            } else if (i === descriptions.length - 2) {
                descriptionText += lastDelimeter;
            }
        }

        return descriptionText;
    }

    /**
     * Helper method that handles user click on toggle
     * @param shouldBeEnabled whether rule should be enabled
     */
    private onEnableDisableInboxRule = (shouldBeEnabled: boolean) => {
        enableDisableInboxRule(this.props.inboxRuleId, shouldBeEnabled);
    };

    /**
     * Helper method that handles user click on delete
     */
    private onRemoveInboxRule = () => {
        const inboxRuleRow = store.inboxRulesListState.inboxRules.get(this.props.inboxRuleId);
        removeInboxRule(inboxRuleRow);
    };

    /**
     * Helper method that handles user click on edit
     * @param shouldMoveUp whether rule should be moved up
     */
    private onMoveRule = (shouldMoveUp: boolean) => {
        const inboxRuleIds = store.inboxRulesListState.inboxRuleIds;
        const ruleToMoveIndex = inboxRuleIds.indexOf(this.props.inboxRuleId);

        let destinationRuleId = shouldMoveUp
            ? inboxRuleIds[ruleToMoveIndex - 1]
            : inboxRuleIds[ruleToMoveIndex + 1];
        // Moves rule to new position
        lazyMoveInboxRule.importAndExecute(
            this.props.inboxRuleId /* idToUpdate */,
            destinationRuleId /* destinationRuleId */,
            false /* isDragAndDrop */
        );
    };

    /**
     * Helper method that handles user click on edit
     */
    private onEditInboxRule = () => {
        editInboxRule(this.props.inboxRuleId);
    };

    /**
     * Helper method that gets alert strings for rules
     * @param isRuleSupported whether rule is supported in OWA
     * @param warningMessages warning messages if the rule is broken
     * @return Alert string
     */
    private getAlertString(isRuleSupported: boolean, warningMessages: string[]): string {
        let alertString = '';
        const spaceDelimeter = ' ';

        if (!isRuleSupported) {
            alertString += strings.options_inboxRules_differentOutlookError + spaceDelimeter;
        }

        if (warningMessages) {
            for (let warningMessage of warningMessages) {
                alertString += warningMessage + spaceDelimeter;
            }
        }

        return alertString;
    }

    private getDragData = () => {
        let inboxRuleId = this.props.inboxRuleId;
        let ruleRowDragData: InboxRuleRowDragData = {
            itemType: DraggableItemTypes.InboxRule,
            ruleId: inboxRuleId,
            ruleName: store.inboxRulesListState.inboxRules.get(inboxRuleId).Name,
        };
        return ruleRowDragData;
    };

    private getDragPreview = (ruleDragData: InboxRuleRowDragData) => {
        var elem = document.createElement('div');
        elem.className = styles.dragHelperStyle;
        elem.innerText = ruleDragData.ruleName;
        return elem;
    };

    private canDrop = (dragData: DragData): boolean => {
        return dragData.itemType === DraggableItemTypes.InboxRule;
    };

    private onDrop = (dragData: DragData) => {
        let draggedRule = dragData as InboxRuleRowDragData;
        let draggedRuleId = draggedRule.ruleId;
        let dragTargetRuleId = this.props.inboxRuleId;

        // Moves rule to new position
        lazyMoveInboxRule.importAndExecute(
            draggedRuleId /* idToUpdate */,
            dragTargetRuleId /* destinationRuleId */,
            true /* isDragAndDrop */
        );
    };

    private runRuleNow = () => {
        const inboxRuleRow = store.inboxRulesListState.inboxRules.get(this.props.inboxRuleId);
        runRuleNow(inboxRuleRow, null /* sourceFolderId */, 'RuleRow');
    };
}
